/**
 * Tailwind CSS implementation of Fieldset component
 * Uses Seeds fieldset CSS classes from fieldset.css
 */

import * as React from "react";
import type { TypeFieldsetProps } from "./FieldsetTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const FieldsetTailwind = ({
  layout = "vertical",
  label,
  helperText,
  children,
  className,
  ...rest
}: TypeFieldsetProps) => {
  // Use a loosely-typed root so DOM attributes in `...rest` (e.g. aria-live) are
  // forwarded without TypeScript flagging styled-system props carried by
  // TypeBoxProps (e.g. `color`) that conflict with the intrinsic <fieldset> attrs.
  // Mirrors ButtonTailwind, which spreads `...rest` onto an `as any` element.
  const Root = "fieldset" as any;

  return (
    <Root
      className={cn("seeds-fieldset", className)}
      data-qa-fieldset=""
      {...rest}
    >
      {typeof label === "string" ? (
        <legend className="seeds-fieldset-legend">{label}</legend>
      ) : (
        label
      )}
      {typeof helperText === "string" ? (
        <p className="seeds-fieldset-helper-text">{helperText}</p>
      ) : (
        helperText
      )}
      <div
        className={cn("seeds-fieldset-list", `seeds-fieldset-list-${layout}`)}
      >
        {React.Children.map(children, (child) => (
          <div
            className={cn(
              "seeds-fieldset-item",
              `seeds-fieldset-item-${layout}`
            )}
          >
            {child}
          </div>
        ))}
      </div>
    </Root>
  );
};

FieldsetTailwind.displayName = "FieldsetTailwind";
